import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';

let nextTooltipId = 0;

// Biểu tượng (i) kèm giải thích khi rê chuột hoặc focus. Bong bóng được dựng ngay dưới <body> với vị trí cố định thay vì nằm trong cây của trang:
// nằm trong cây thì bị các vùng cuộn (overflow) cắt, và bị che bởi bất kỳ thẻ nào có z-index hoặc stacking context riêng (thead dính, card, bảng cuộn...).
// Giao diện bong bóng (màu, chữ, bo góc) nằm ở class toàn cục .info-tooltip-bubble trong styles.scss; ở đây chỉ đặt phần chức năng (vị trí cố định, z-index, đo chỗ).
@Component({
  selector: 'app-info-tooltip',
  templateUrl: './info-tooltip.component.html',
  styleUrls: ['./info-tooltip.component.scss']
})
export class InfoTooltipComponent implements OnDestroy {
  @Input() text: string | null | undefined = null;
  /** Where the bubble opens; 'top' flips to the bottom (and 'bottom' to the top) when there is no room. */
  @Input() position: 'top' | 'bottom' = 'top';
  /** Which edge of the icon the bubble lines up with; the bubble is always kept inside the window. */
  @Input() align: 'center' | 'left' | 'right' = 'center';

  @ViewChild('icon') icon?: ElementRef<HTMLElement>;

  private static readonly MARGIN = 8;
  private static readonly GAP = 6;
  private static readonly MAX_WIDTH = 260;

  private readonly bubbleId = `info-tooltip-${++nextTooltipId}`;
  private bubble: HTMLElement | null = null;
  private readonly onViewportChange = () => this.placeBubble();

  show(): void {
    const icon = this.icon?.nativeElement;
    if (!this.text || !icon || typeof document === 'undefined') return;
    this.hide();

    const bubble = document.createElement('div');
    bubble.id = this.bubbleId;
    bubble.setAttribute('role', 'tooltip');
    bubble.className = 'info-tooltip-bubble';
    bubble.textContent = this.text;
    Object.assign(bubble.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      zIndex: '2147483000',
      width: 'max-content',
      pointerEvents: 'none',
      visibility: 'hidden'
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(bubble);
    this.bubble = bubble;
    icon.setAttribute('aria-describedby', this.bubbleId);

    this.placeBubble();
    if (this.bubble) bubble.style.visibility = 'visible';
    // Cuộn (kể cả trong vùng cuộn con) hoặc đổi cỡ cửa sổ làm icon dời chỗ: đặt lại bong bóng theo icon thay vì để nó trôi lệch.
    document.addEventListener('scroll', this.onViewportChange, true);
    window.addEventListener('resize', this.onViewportChange);
  }

  hide(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('scroll', this.onViewportChange, true);
      window.removeEventListener('resize', this.onViewportChange);
    }
    this.icon?.nativeElement.removeAttribute('aria-describedby');
    this.bubble?.remove();
    this.bubble = null;
  }

  ngOnDestroy(): void {
    this.hide();
  }

  private placeBubble(): void {
    const bubble = this.bubble;
    const icon = this.icon?.nativeElement.getBoundingClientRect();
    if (!bubble || !icon) return;

    // Icon đã cuộn ra khỏi màn hình thì đóng, không để bong bóng đứng một mình.
    if (icon.bottom < 0 || icon.top > window.innerHeight || icon.right < 0 || icon.left > window.innerWidth) {
      this.hide();
      return;
    }

    // Màn hẹp hơn bong bóng thì bong bóng co lại để vẫn nằm trọn trong màn hình.
    bubble.style.maxWidth = `${Math.min(InfoTooltipComponent.MAX_WIDTH, window.innerWidth - 2 * InfoTooltipComponent.MARGIN)}px`;
    bubble.style.left = `${Math.round(this.leftFor(icon, bubble.offsetWidth))}px`;
    bubble.style.top = `${Math.round(this.topFor(icon, bubble.offsetHeight))}px`;
  }

  private leftFor(icon: DOMRect, width: number): number {
    let start: number;
    switch (this.align) {
      case 'left': start = icon.left; break;
      case 'right': start = icon.right - width; break;
      default: start = icon.left + icon.width / 2 - width / 2;
    }
    const margin = InfoTooltipComponent.MARGIN;
    return Math.max(margin, Math.min(start, window.innerWidth - width - margin));
  }

  private topFor(icon: DOMRect, height: number): number {
    const margin = InfoTooltipComponent.MARGIN;
    const above = icon.top - height - InfoTooltipComponent.GAP;
    const below = icon.bottom + InfoTooltipComponent.GAP;
    if (this.position === 'top') return above < margin ? below : above;
    return below + height > window.innerHeight - margin && above >= margin ? above : below;
  }
}
